import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (username, password) => {
    const masked = password.length > 6
      ? password.slice(0, 3) + '*'.repeat(password.length - 6) + password.slice(-3)
      : '***';
    console.log('Login attempt, password:', masked);
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    return api.post('/auth/login', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getCurrentUser: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    api.put('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
  // Login-page self-service change - no session/token required, but the
  // caller must supply the current valid password.
  changePasswordPublic: (username, currentPassword, newPassword) =>
    api.put('/auth/change-password-public', {
      username,
      current_password: currentPassword,
      new_password: newPassword,
    }),
};

// Department APIs
export const departmentAPI = {
  getAll: () => api.get('/departments/'),
  getById: (id) => api.get(`/departments/${id}`),
  create: (data) => api.post('/departments/', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`),
};

// Employee Group APIs
export const employeeGroupAPI = {
  getAll: () => api.get('/employee-groups/'),
  getById: (id) => api.get(`/employee-groups/${id}`),
  create: (data) => api.post('/employee-groups/', data),
  update: (id, data) => api.put(`/employee-groups/${id}`, data),
  delete: (id) => api.delete(`/employee-groups/${id}`),
  reorder: (groupIds) => api.put('/employee-groups/reorder', { group_ids: groupIds }),
};

// Employee APIs
export const employeeAPI = {
  getAll: () => api.get('/employees/'),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees/', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  getLeaveSummary: () => api.get('/employees/leave-summary'),
  // Minimal id+name list - doesn't require admin.employees, safe for
  // pickers in features (like Salary Advance) that shouldn't also unlock
  // the full Admin Portal Employees tab.
  getLookup: () => api.get('/employees/lookup'),
};

// Holiday APIs
export const holidayAPI = {
  getAll: () => api.get('/holidays/'),
  getById: (id) => api.get(`/holidays/${id}`),
  create: (data) => api.post('/holidays/', data),
  update: (id, data) => api.put(`/holidays/${id}`, data),
  delete: (id) => api.delete(`/holidays/${id}`),
};

// Bank APIs
export const bankAPI = {
  getAll: () => api.get('/banks/'),
  create: (data) => api.post('/banks/', data),
  update: (id, data) => api.put(`/banks/${id}`, data),
  delete: (id) => api.delete(`/banks/${id}`),
};

// System Config APIs
export const systemConfigAPI = {
  getAll: () => api.get('/system-config/'),
  getByKey: (key) => api.get(`/system-config/${key}`),
  update: (data) => api.put('/system-config/', data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/system-config/upload-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getCurrentLogo: () => api.get('/system-config/logo/current'),
};

// Attendance APIs
export const attendanceAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/attendance/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getRegularReport: (month, year) =>
    api.get('/attendance/report/regular', { params: { month, year } }),
  getEmployeesLookup: () => api.get('/attendance/employees-lookup'),
  // months: array of {month, year} -> comma-separated "YYYY-MM" tokens
  getEmployeeMonthsReport: (empId, months) =>
    api.get('/attendance/report/employee-months', {
      params: { emp_id: empId, months: months.map(({ month, year }) => `${year}-${String(month).padStart(2, '0')}`).join(',') },
    }),
  exportRegular: (month, year) =>
    api.get('/attendance/export/regular', {
      params: { month, year },
      responseType: 'blob',
    }),
  exportOT: (month, year) =>
    api.get('/attendance/export/ot', {
      params: { month, year },
      responseType: 'blob',
    }),
  previewCleanup: (month, year) =>
    api.get('/attendance/cleanup/preview', { params: { month, year } }),
  deleteMonth: (month, year) =>
    api.delete('/attendance/cleanup', { params: { month, year } }),
};

// Flexi Days APIs
export const flexiDaysAPI = {
  getAll: (month, year) => api.get('/flexi-days/', { params: { month, year } }),
  getByEmpId: (empId, month, year) => 
    api.get(`/flexi-days/${empId}`, { params: { month, year } }),
  createOrUpdate: (data) => api.post('/flexi-days/', data),
  bulkSave: (items) => api.post('/flexi-days/bulk', { items }),
  update: (empId, month, year, data) => 
    api.put(`/flexi-days/${empId}`, data, { params: { month, year } }),
  delete: (empId, month, year) => 
    api.delete(`/flexi-days/${empId}`, { params: { month, year } }),
};

// Attendance Adjustments APIs
export const attendanceAdjustmentsAPI = {
  getAll: (month, year) =>
    api.get('/attendance-adjustments/', { params: { month, year } }),
  create: (data) =>
    api.post('/attendance-adjustments/', data),
  bulkSave: (items) =>
    api.post('/attendance-adjustments/bulk', { items }),
  update: (empId, adjustmentDate, data) =>
    api.put(`/attendance-adjustments/${empId}`, data, {
      params: { adjustment_date: adjustmentDate }
    }),
  delete: (empId, adjustmentDate) =>
    api.delete(`/attendance-adjustments/${empId}`, {
      params: { adjustment_date: adjustmentDate }
    }),
};

// Employee Bank Account APIs
export const employeeBankAccountAPI = {
  getAll: (empId) => api.get(`/employees/${empId}/bank-accounts`),
  create: (empId, data) => api.post(`/employees/${empId}/bank-accounts`, data),
  update: (empId, accountId, data) => api.put(`/employees/${empId}/bank-accounts/${accountId}`, data),
  delete: (empId, accountId) => api.delete(`/employees/${empId}/bank-accounts/${accountId}`),
};

// Holiday Groups APIs
export const holidayGroupAPI = {
  getAll: () => api.get('/holiday-groups'),
  getById: (groupId) => api.get(`/holiday-groups/${groupId}`),
  create: (data) => api.post('/holiday-groups', data),
  update: (groupId, data) => api.put(`/holiday-groups/${groupId}`, data),
  delete: (groupId) => api.delete(`/holiday-groups/${groupId}`),
  getHolidays: (groupId) => api.get(`/holiday-groups/${groupId}/holidays`),
  assignHolidays: (groupId, holidayIds) => 
    api.post(`/holiday-groups/${groupId}/holidays`, {
      group_id: groupId,
      holiday_ids: holidayIds,
    }),
  removeHoliday: (groupId, holidayId) => 
    api.delete(`/holiday-groups/${groupId}/holidays/${holidayId}`),
};

// Payroll: Configure Salary APIs
export const salaryConfigAPI = {
  getAll: () => api.get('/payroll/salary-config'),
  bulkSave: (items) => api.post('/payroll/salary-config/bulk', { items }),
};

// User APIs (admin only)
export const usersAPI = {
  getAll: () => api.get('/users/'),
  create: (data) => api.post('/users/', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, newPassword) =>
    api.put(`/users/${id}/reset-password`, { new_password: newPassword }),
};

// Role APIs (admin only)
export const rolesAPI = {
  getAll: () => api.get('/roles/'),
  getPermissionCatalog: () => api.get('/roles/permission-catalog'),
  create: (data) => api.post('/roles/', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
};

// Folk Saptapur APIs (student attendance / sadhana tracking)
export const folkSaptapurAPI = {
  getStudents: () => api.get('/folk-saptapur/students'),
  createStudent: (data) => api.post('/folk-saptapur/students', data),
  deleteStudent: (id) => api.delete(`/folk-saptapur/students/${id}`),
  reorderStudents: (studentIds) => api.put('/folk-saptapur/students/reorder', { student_ids: studentIds }),

  getServiceTasksOverview: () => api.get('/folk-saptapur/service-tasks/overview'),
  updateCompletion: (data) => api.put('/folk-saptapur/service-tasks/completion', data),

  listServiceTasks: (category) =>
    api.get('/folk-saptapur/service-tasks', { params: { category } }),
  assignServiceTask: (category, data) =>
    api.post(`/folk-saptapur/service-tasks/${category}`, data),
  removeServiceTask: (taskId) => api.delete(`/folk-saptapur/service-tasks/${taskId}`),

  getSpecialClass: () => api.get('/folk-saptapur/special-class'),
  saveSpecialClass: (data) => api.put('/folk-saptapur/special-class', data),

  getDayAttendance: (attendanceDate) =>
    api.get('/folk-saptapur/attendance', { params: { attendance_date: attendanceDate } }),
  saveDayAttendance: (data) => api.post('/folk-saptapur/attendance', data),

  getReport: (month, year) =>
    api.get('/folk-saptapur/report', { params: { month, year } }),
  exportReport: (month, year, includeNative) =>
    api.get('/folk-saptapur/report/export', {
      params: { month, year, include_native: includeNative },
      responseType: 'blob',
    }),
};

// Salary Advance APIs
export const salaryAdvanceAPI = {
  getNormal: () => api.get('/salary-advance/normal'),
  createNormal: (data) => api.post('/salary-advance/normal', data),
  updateNormal: (id, data) => api.put(`/salary-advance/normal/${id}`, data),
  deleteNormal: (id) => api.delete(`/salary-advance/normal/${id}`),

  getEmi: () => api.get('/salary-advance/emi'),
  createEmi: (data) => api.post('/salary-advance/emi', data),
  updateEmi: (id, data) => api.put(`/salary-advance/emi/${id}`, data),
  deleteEmi: (id) => api.delete(`/salary-advance/emi/${id}`),
  // overrideAmount: null clears it (normal deduction resumes), 0 skips the
  // next deduction, any other number deducts that amount just once.
  setEmiNextDeductionOverride: (id, overrideAmount) =>
    api.put(`/salary-advance/emi/${id}/next-deduction-override`, { override_amount: overrideAmount }),

  getSalaryEligible: (empId, month, year) =>
    api.get('/salary-advance/salary-eligible', { params: { emp_id: empId, month, year } }),

  getReport: (month, year) =>
    api.get('/salary-advance/report', { params: { month, year } }),

  getCurrentMonth: () => api.get('/salary-advance/current-month'),
};

// Help APIs
export const helpAPI = {
  downloadPdf: () => api.get('/help/pdf', { responseType: 'blob' }),
};

// Vendor Payments APIs
export const vendorPaymentsAPI = {
  getVendors: () => api.get('/vendor-payments/vendors'),
  createVendor: (data) => api.post('/vendor-payments/vendors', data),
  updateVendor: (id, data) => api.put(`/vendor-payments/vendors/${id}`, data),
  deleteVendor: (id) => api.delete(`/vendor-payments/vendors/${id}`),

  getPayments: () => api.get('/vendor-payments/payments'),
  createPayment: (data) => api.post('/vendor-payments/payments', data),
  updatePayment: (id, data) => api.put(`/vendor-payments/payments/${id}`, data),
  deletePayment: (id) => api.delete(`/vendor-payments/payments/${id}`),
};

export default api;
